import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { resolveActiveTalentUsers } from './talentEmailRouting';

const log = createComponentLogger('RecruiterNotificationService', 'recruitment-notifications');

/**
 * Recruiter Notification Service - Recruitment Workflow Notifications
 *
 * This service sends email notifications to Talent owners via Resend for recruitment milestones:
 * 1. Invitation sent - Confirms recruitment invite was sent to candidate
 * 2. LO signed contract - Alerts when candidate signs employment agreement
 * 3. Company counter-signed - Alerts when company finalizes contract
 * 4. Onboarding complete - Alerts when candidate finishes all onboarding tasks
 *
 * Notifications are sent to:
 * - The user who sent the recruiting invite, when available
 * - The assigned recruiter (from recruits.recruiter_id), when different
 * - join@lendwisemtg.com and explicitly configured extras as CC via Email Notifications
 *
 * Uses Resend API via send-notification Edge Function (internal notifications only).
 */

export type RecruiterNotificationType =
  | 'recruiter_invitation_sent'
  | 'recruiter_lo_signed'
  | 'recruiter_company_signed'
  | 'recruiter_onboarding_complete'
  | 'nmls_authorization_granted';

export interface NotificationResponse {
  success: boolean;
  emailId?: string;
  message: string;
  error?: string;
}

export interface MultiNotificationResponse {
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  results: Array<{
    userId: string;
    email: string;
    success: boolean;
    emailId?: string;
    error?: string;
  }>;
}

interface RecruitInfo {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  recruiterId: string | null;
}

interface Recipient {
  id: string;
  email: string;
  fullName: string;
}

/**
 * Service for sending recruitment workflow notifications to Talent owners via Resend
 */
class RecruiterNotificationService {
  private async getCurrentUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id ?? null;
    } catch {
      return null;
    }
  }

  private async getLatestInviteSenderId(recruitId: string): Promise<string | null> {
    try {
      // Authoritative invite sender lives on the recruit (trigger-enforced to the
      // sending user at send time), not the mutable communications log.
      const { data, error } = await supabase
        .from('recruits')
        .select('last_invited_by')
        .eq('id', recruitId)
        .maybeSingle();

      if (error) {
        log.warn('Failed to resolve recruit invite sender', { recruitId, error: error.message });
        return null;
      }

      return typeof data?.last_invited_by === 'string' ? data.last_invited_by : null;
    } catch (error) {
      log.warn('Error resolving recruit invite sender', {
        recruitId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      return null;
    }
  }

  private async getOwnerRecipients(
    recruitId: string,
    recruiterId: string | null | undefined,
    senderId?: string | null
  ): Promise<Recipient[]> {
    try {
      const recipients = await resolveActiveTalentUsers([senderId, recruiterId]);
      log.info('Fetched Talent owner notification recipients', {
        recruitId,
        totalRecipients: recipients.length,
        ownerUserIds: recipients.map((recipient) => recipient.id)
      });
      return recipients;
    } catch (error) {
      log.error('Error getting Talent owner recipients', {
        recruitId,
        recruiterId,
        senderId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);
      return [];
    }
  }

  private async sendToOwners(
    recruitId: string | null,
    recipients: Recipient[],
    notificationType: RecruiterNotificationType,
    notificationData: Record<string, unknown>
  ): Promise<Array<{ userId: string; email: string; success: boolean; emailId?: string; error?: string }>> {
    const usesRecruitOwnerRouting = Boolean(recruitId);
    const resultRecipients = usesRecruitOwnerRouting && recipients.length > 0
      ? recipients
      : [{ id: 'configured_talent_recipients', email: 'configured Talent recipients', fullName: 'Talent team' }];

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      const recipientName = usesRecruitOwnerRouting && recipients.length === 1 ? recipients[0].fullName : 'team';
      const { data, error } = await supabase.functions.invoke('send-notification', {
        body: {
          ...(recruitId
            ? { talentOwnerRecruitId: recruitId }
            : { resolveAdditionalRecipientsAsPrimary: true }),
          notificationType,
          data: {
            ...notificationData,
            recipientName
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        return resultRecipients.map((recipient) => ({
          userId: recipient.id,
          email: recipient.email,
          success: false,
          error: error.message
        }));
      }

      return resultRecipients.map((recipient) => ({
        userId: recipient.id,
        email: recipient.email,
        success: true,
        emailId: data.emailId
      }));
    } catch (error) {
      return resultRecipients.map((recipient) => ({
        userId: recipient.id,
        email: recipient.email,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    }
  }

  /**
   * Send notification when recruitment invitation is sent to candidate
   */
  async notifyRecruiterInvitationSent(
    recruit: RecruitInfo,
    invitationUrl: string,
    expiresAt: string
  ): Promise<MultiNotificationResponse> {
    try {
      log.info('Sending recruiter invitation sent notifications', {
        recruitId: recruit.id,
        recruiterId: recruit.recruiterId,
        candidateEmail: recruit.email
      });

      const senderId = await this.getCurrentUserId();
      const recipients = await this.getOwnerRecipients(recruit.id, recruit.recruiterId, senderId);

      if (recipients.length === 0) {
        log.warn('No owner recipients found; falling back to configured Talent recipients', { recruitId: recruit.id });
      }

      const notificationData = {
        candidateFirstName: recruit.firstName,
        candidateLastName: recruit.lastName,
        candidateEmail: recruit.email,
        invitationUrl,
        expiresAt,
        recruitDashboardUrl: `${window.location.origin}/internal?section=recruiting&candidate=${recruit.id}`
      };

      const results = await this.sendToOwners(
        recruit.id,
        recipients,
        'recruiter_invitation_sent',
        notificationData
      );

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      const totalRecipients = results.length;

      log.info('Recruiter invitation sent notifications completed', {
        recruitId: recruit.id,
        totalRecipients,
        successCount,
        failureCount
      });

      return {
        totalRecipients,
        successCount,
        failureCount,
        results
      };
    } catch (error) {
      log.error('Error in notifyRecruiterInvitationSent', {
        recruitId: recruit.id,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        totalRecipients: 0,
        successCount: 0,
        failureCount: 0,
        results: []
      };
    }
  }

  /**
   * Send notification when candidate (loan officer) signs contract
   */
  async notifyRecruiterLOSigned(
    recruit: RecruitInfo,
    signatureDate: string,
    contractId: string
  ): Promise<MultiNotificationResponse> {
    try {
      log.info('Sending recruiter LO signed notifications', {
        recruitId: recruit.id,
        recruiterId: recruit.recruiterId,
        contractId
      });

      const senderId = await this.getLatestInviteSenderId(recruit.id);
      const recipients = await this.getOwnerRecipients(recruit.id, recruit.recruiterId, senderId);

      if (recipients.length === 0) {
        log.warn('No owner recipients found; falling back to configured Talent recipients', { recruitId: recruit.id });
      }

      const notificationData = {
        candidateFirstName: recruit.firstName,
        candidateLastName: recruit.lastName,
        signatureDate,
        contractId,
        contractReviewUrl: `${window.location.origin}/internal?section=recruiting&candidate=${recruit.id}&tab=contract-details&action=review-contract`
      };

      const results = await this.sendToOwners(
        recruit.id,
        recipients,
        'recruiter_lo_signed',
        notificationData
      );

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      const totalRecipients = results.length;

      log.info('Recruiter LO signed notifications completed', {
        recruitId: recruit.id,
        totalRecipients,
        successCount,
        failureCount
      });

      return {
        totalRecipients,
        successCount,
        failureCount,
        results
      };
    } catch (error) {
      log.error('Error in notifyRecruiterLOSigned', {
        recruitId: recruit.id,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        totalRecipients: 0,
        successCount: 0,
        failureCount: 0,
        results: []
      };
    }
  }

  /**
   * Send notification when company counter-signs contract
   */
  async notifyRecruiterCompanySigned(
    recruit: RecruitInfo,
    signatureDate: string,
    contractId: string,
    effectiveDate?: string
  ): Promise<MultiNotificationResponse> {
    try {
      log.info('Sending recruiter company signed notifications', {
        recruitId: recruit.id,
        recruiterId: recruit.recruiterId,
        contractId
      });

      const senderId = await this.getLatestInviteSenderId(recruit.id);
      const recipients = await this.getOwnerRecipients(recruit.id, recruit.recruiterId, senderId);

      if (recipients.length === 0) {
        log.warn('No owner recipients found; falling back to configured Talent recipients', { recruitId: recruit.id });
      }

      const notificationData = {
        candidateFirstName: recruit.firstName,
        candidateLastName: recruit.lastName,
        signatureDate,
        contractId,
        effectiveDate: effectiveDate || signatureDate,
        recruitDashboardUrl: `${window.location.origin}/internal?section=recruiting&candidate=${recruit.id}`
      };

      const results = await this.sendToOwners(
        recruit.id,
        recipients,
        'recruiter_company_signed',
        notificationData
      );

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      const totalRecipients = results.length;

      log.info('Recruiter company signed notifications completed', {
        recruitId: recruit.id,
        totalRecipients,
        successCount,
        failureCount
      });

      return {
        totalRecipients,
        successCount,
        failureCount,
        results
      };
    } catch (error) {
      log.error('Error in notifyRecruiterCompanySigned', {
        recruitId: recruit.id,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        totalRecipients: 0,
        successCount: 0,
        failureCount: 0,
        results: []
      };
    }
  }

  /**
   * Send notification when candidate completes all onboarding tasks
   */
  async notifyRecruiterOnboardingComplete(
    recruit: RecruitInfo,
    completedTasks: Array<{ id: string; title: string }>,
    completionDate: string
  ): Promise<MultiNotificationResponse> {
    try {
      log.info('Sending recruiter onboarding complete notifications', {
        recruitId: recruit.id,
        recruiterId: recruit.recruiterId,
        tasksCompleted: completedTasks.length
      });

      const senderId = await this.getLatestInviteSenderId(recruit.id);
      const recipients = await this.getOwnerRecipients(recruit.id, recruit.recruiterId, senderId);

      if (recipients.length === 0) {
        log.warn('No owner recipients found; falling back to configured Talent recipients', { recruitId: recruit.id });
      }

      const notificationData = {
        candidateFirstName: recruit.firstName,
        candidateLastName: recruit.lastName,
        completionDate,
        tasksCompleted: completedTasks,
        recruitDashboardUrl: `${window.location.origin}/internal?section=recruiting&candidate=${recruit.id}`
      };

      const results = await this.sendToOwners(
        recruit.id,
        recipients,
        'recruiter_onboarding_complete',
        notificationData
      );

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      const totalRecipients = results.length;

      log.info('Recruiter onboarding complete notifications completed', {
        recruitId: recruit.id,
        totalRecipients,
        successCount,
        failureCount
      });

      return {
        totalRecipients,
        successCount,
        failureCount,
        results
      };
    } catch (error) {
      log.error('Error in notifyRecruiterOnboardingComplete', {
        recruitId: recruit.id,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        totalRecipients: 0,
        successCount: 0,
        failureCount: 0,
        results: []
      };
    }
  }

  /**
   * Send notification when NMLS authorization is granted to a new loan officer
   * This is a celebration notification sent to configured recipients
   */
  async notifyNmlsAuthorizationGranted(data: {
    recruitId?: string;
    loFirstName: string;
    loLastName: string;
    nmlsNumber: string;
    stateLicenses: string[];
    authorizationDate: string;
    recruiterName?: string;
    recruiterId?: string;
  }): Promise<MultiNotificationResponse> {
    try {
      log.info('Sending NMLS authorization granted notifications', {
        loName: `${data.loFirstName} ${data.loLastName}`,
        nmlsNumber: data.nmlsNumber,
        stateLicenses: data.stateLicenses
      });

      const senderId = data.recruitId
        ? await this.getLatestInviteSenderId(data.recruitId)
        : await this.getCurrentUserId();
      const recipients = await this.getOwnerRecipients(
        data.recruitId || 'nmls_authorization',
        data.recruiterId,
        senderId
      );

      if (recipients.length === 0) {
        log.warn('No owner recipients found for NMLS authorization notification; falling back to configured Talent recipients');
      }

      const notificationData = {
        loFirstName: data.loFirstName,
        loLastName: data.loLastName,
        nmlsNumber: data.nmlsNumber,
        stateLicenses: data.stateLicenses,
        authorizationDate: data.authorizationDate,
        recruiterName: data.recruiterName,
        recruitDashboardUrl: `${window.location.origin}/internal?section=recruiting`
      };

      const results = await this.sendToOwners(
        data.recruitId ?? null,
        recipients,
        'nmls_authorization_granted',
        notificationData
      );

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      const totalRecipients = results.length;

      log.info('NMLS authorization granted notifications completed', {
        loName: `${data.loFirstName} ${data.loLastName}`,
        totalRecipients,
        successCount,
        failureCount
      });

      return {
        totalRecipients,
        successCount,
        failureCount,
        results
      };
    } catch (error) {
      log.error('Error in notifyNmlsAuthorizationGranted', {
        loName: `${data.loFirstName} ${data.loLastName}`,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error as Error);

      return {
        totalRecipients: 0,
        successCount: 0,
        failureCount: 0,
        results: []
      };
    }
  }
}

// Export singleton instance
export const recruiterNotificationService = new RecruiterNotificationService();
